import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateToken, fetchAllTokens, revokeToken, extendToken, deleteToken, updateToken, verifyAdminSession, adminLogout, fetchDocumentSets } from '../services/api';
import TokenList from '../components/TokenList';
import Toast from '../components/Toast';
import { useToast } from '../hooks/useToast';
import { updateSiteSetting } from '../services/api';

export default function Admin() {
  const navigate = useNavigate();

  // Vérifier l'authentification via JWT session (V2)
  useEffect(() => {
    verifyAdminSession().then(valid => {
      if (!valid) navigate('/admin/login');
    });
  }, [navigate]);
  // V2 : mode de partage (fichier unique V1 ou ensemble V2)
  const [shareMode, setShareMode] = useState('set'); // 'file' (V1) ou 'set' (V2)

  const [formData, setFormData] = useState({
    email: '',
    company: '',
    name: '',
    file_name: 'cv.pdf',
    download_filename: 'CV.pdf',
    set_id: null,
  });

  // Liste des fichiers disponibles (chargée dynamiquement)
  const [availableFiles, setAvailableFiles] = useState([]);
  // V2 : Liste des ensembles de documents
  const [documentSets, setDocumentSets] = useState([]);
  
  const { toast, showToast: showFlashMessage, hideToast } = useToast();

  // Charger les fichiers et ensembles au montage
  useEffect(() => {
    const loadAvailableFiles = async () => {
      try {
        const API_URL = import.meta.env.VITE_API_URL || '/cv/api';
        const url = API_URL.endsWith('/api') 
          ? `${API_URL}/files/list` 
          : `${API_URL}/api/files/list`;
        const response = await fetch(url);
        const data = await response.json();
        
        if (data.success && data.files) {
          const filesFormatted = data.files.map(file => ({
            value: file.filename,
            label: file.label,
            icon: file.icon,
            description: file.description
          }));
          setAvailableFiles(filesFormatted);
        }
      } catch (error) {
        console.error('Erreur chargement fichiers:', error);
        setAvailableFiles([
          { value: 'cv.pdf', label: 'CV Standard', icon: '📄', description: 'CV généraliste' }
        ]);
      }
    };
    
    const loadDocumentSets = async () => {
      try {
        const sets = await fetchDocumentSets();
        setDocumentSets(sets);
        // Si des ensembles existent, pré-sélectionner le premier
        if (sets.length > 0) {
          setFormData(prev => ({ ...prev, set_id: sets[0].id }));
        }
      } catch (error) {
        console.error('Erreur chargement ensembles:', error);
      }
    };

    loadAvailableFiles();
    loadDocumentSets();
  }, []);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState({ defaultZoom: 180, allowDownload: true });
  const [tokens, setTokens] = useState([]);
  const [loadingTokens, setLoadingTokens] = useState(true);
  const [stats, setStats] = useState({
    active_tokens: 0,
    total_views: 0,
    total_downloads: 0,
  });

  // Charger les settings viewer depuis la DB
  useEffect(() => {
    const API_URL = import.meta.env.VITE_API_URL || '/cv/api';
    fetch(`${API_URL}/site-settings`)
      .then(r => r.json())
      .then(d => {
        const s = d.settings || {};
        setSettings({
          defaultZoom: s.viewer_default_zoom ? parseInt(s.viewer_default_zoom) : 180,
          allowDownload: s.viewer_allow_download !== 'false',
        });
      })
      .catch(() => {});
  }, []);

  // Charger les tokens au montage
  useEffect(() => {
    const loadTokens = async () => {
      try {
        const data = await fetchAllTokens();
        setTokens(data);
        
        // Calculer les statistiques
        const now = new Date();
        const activeTokens = data.filter(t => 
          !t.is_revoked && new Date(t.token_expires_at) > now
        ).length;
        const totalViews = data.reduce((sum, t) => sum + (t.view_count || 0), 0);
        const totalDownloads = data.reduce((sum, t) => sum + (t.download_count || 0), 0);
        
        setStats({
          active_tokens: activeTokens,
          total_views: totalViews,
          total_downloads: totalDownloads,
        });
      } catch (err) {
        console.error('Erreur chargement tokens:', err);
      } finally {
        setLoadingTokens(false);
      }
    };
    loadTokens();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setGeneratedLink(null);

    try {
      // Envoyer set_id seulement en mode V2, sinon fichier unique V1
      const payload = {
        email: formData.email,
        company: formData.company,
        name: formData.name,
      };

      if (shareMode === 'set' && formData.set_id) {
        payload.set_id = formData.set_id;
      } else {
        payload.file_name = formData.file_name;
        payload.download_filename = formData.download_filename;
      }

      const response = await generateToken(payload);
      setGeneratedLink(response);
      // Réinitialiser les champs recruteur (garder la sélection de fichier/ensemble)
      setFormData(prev => ({ 
        ...prev,
        email: '', 
        company: '', 
        name: '',
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedLink.viewUrl);
    showFlashMessage('Lien copié dans le presse-papiers !');
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    const dbKey = key === 'defaultZoom' ? 'viewer_default_zoom' : 'viewer_allow_download';
    const dbValue = key === 'defaultZoom' ? String(value) : String(value);
    updateSiteSetting(dbKey, dbValue).catch(() => {});
  };

  const handleLogout = () => {
    adminLogout();
    navigate('/admin/login');
  };

  const handleFileSelect = (fileName) => {
    // Trouver le fichier sélectionné pour récupérer son label
    const selectedFile = availableFiles.find(f => f.value === fileName);
    const defaultDownloadName = selectedFile?.label 
      ? `${selectedFile.label.replace(/\s+/g, '-')}.pdf`
      : fileName;
    
    setFormData(prev => ({
      ...prev,
      file_name: fileName,
      download_filename: defaultDownloadName
    }));
  };

  const handleRevoke = async (tokenId) => {
    try {
      // Appeler l'API backend pour révoquer en DB
      await revokeToken(tokenId);
      
      // Mettre à jour le state local
      setTokens(tokens.map(t => 
        t.id === tokenId ? { ...t, is_revoked: true } : t
      ));
      showFlashMessage('Token révoqué avec succès');
    } catch (err) {
      console.error('Erreur révocation:', err);
      showFlashMessage('Erreur lors de la révocation du token', 'error');
    }
  };

  const handleExtend = async (tokenId) => {
    try {
      // Appeler l'API backend pour prolonger en DB
      const response = await extendToken(tokenId, 48);
      
      // Mettre à jour le state local avec la nouvelle date
      setTokens(tokens.map(t => {
        if (t.id === tokenId) {
          return { ...t, token_expires_at: response.new_expires_at };
        }
        return t;
      }));
      showFlashMessage('Token prolongé de 48 heures');
    } catch (err) {
      console.error('Erreur prolongation:', err);
      showFlashMessage('Erreur lors de la prolongation du token', 'error');
    }
  };

  const handleRefresh = async () => {
    try {
      setLoadingTokens(true);
      const data = await fetchAllTokens();
      setTokens(data);
      
      // Recalculer les statistiques
      const now = new Date();
      const activeTokens = data.filter(t => 
        !t.is_revoked && new Date(t.token_expires_at) > now
      ).length;
      const totalViews = data.reduce((sum, t) => sum + (t.view_count || 0), 0);
      const totalDownloads = data.reduce((sum, t) => sum + (t.download_count || 0), 0);
      
      setStats({
        active_tokens: activeTokens,
        total_views: totalViews,
        total_downloads: totalDownloads,
      });
      
      showFlashMessage('Liste actualisée');
    } catch (err) {
      showFlashMessage('Erreur lors de l\'actualisation', 'error');
    } finally {
      setLoadingTokens(false);
    }
  };

  const handleDelete = async (tokenId) => {
    try {
      await deleteToken(tokenId);
      // Retirer le token de la liste
      setTokens(tokens.filter(t => t.id !== tokenId));
      showFlashMessage('Token supprimé avec succès');
    } catch (error) {
      console.error('Erreur suppression token:', error);
      showFlashMessage('Erreur lors de la suppression du token', 'error');
    }
  };

  const handleUpdate = async (tokenId, data) => {
    try {
      await updateToken(tokenId, data);
      // Recharger la liste des tokens
      await handleRefresh();
      showFlashMessage('Token modifié avec succès');
    } catch (error) {
      console.error('Erreur modification token:', error);
      showFlashMessage('Erreur lors de la modification du token', 'error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              🔐 Administration CV Viewer
            </h1>
            <p className="text-gray-600">
              Générez des liens sécurisés pour partager votre CV avec les recruteurs
            </p>
          </div>
          <div className="flex items-center space-x-3 flex-wrap gap-y-2">
            <button
              onClick={() => navigate('/admin/document-sets')}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center space-x-2 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <span>Ensembles de documents</span>
            </button>
            <button
              onClick={() => navigate('/admin/manage-files')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Gérer les documents</span>
            </button>
            <button
              onClick={() => navigate('/admin/settings')}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 flex items-center space-x-2 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>Paramètres</span>
            </button>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-white text-red-600 border border-red-200 rounded-lg hover:bg-red-50 flex items-center space-x-2 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span>Déconnexion</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Formulaire de génération */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              📧 Générer un nouveau lien
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email du recruteur *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="recruteur@entreprise.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Entreprise *
                </label>
                <input
                  type="text"
                  required
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Nom de l'entreprise"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom du recruteur *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Jean Dupont"
                />
              </div>

              {/* V2 : Mode de partage */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mode de partage
                </label>
                <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setShareMode('set')}
                    className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
                      shareMode === 'set'
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Ensemble de documents (V2)
                  </button>
                  <button
                    type="button"
                    onClick={() => setShareMode('file')}
                    className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors border-l border-gray-300 ${
                      shareMode === 'file'
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Fichier unique (V1)
                  </button>
                </div>
              </div>

              {/* V2 : Sélecteur d'ensemble */}
              {shareMode === 'set' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ensemble de documents à partager
                  </label>
                  {documentSets.length > 0 ? (
                    <div className="space-y-2">
                      {documentSets.map(set => (
                        <button
                          key={set.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, set_id: set.id })}
                          className={`w-full text-left p-3 rounded-lg border-2 transition-all duration-200 ${
                            formData.set_id === set.id
                              ? 'border-primary-600 bg-primary-50 shadow-sm'
                              : 'border-gray-200 bg-white hover:border-primary-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900">{set.label}</p>
                              {set.description && (
                                <p className="text-xs text-gray-500 mt-0.5">{set.description}</p>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {set.document_count || 0} doc{(set.document_count || 0) > 1 ? 's' : ''}
                              </span>
                              {formData.set_id === set.id && (
                                <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                      <p className="text-sm text-gray-500 mb-2">Aucun ensemble créé</p>
                      <button
                        type="button"
                        onClick={() => navigate('/admin/document-sets')}
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                      >
                        Créer un ensemble →
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* V1 : Sélecteur de fichier unique */}
              {shareMode === 'file' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Choisir le fichier PDF à envoyer
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {availableFiles.map(file => (
                        <button
                          key={file.value}
                          type="button"
                          onClick={() => handleFileSelect(file.value)}
                          className={`
                            relative p-4 rounded-lg border-2 transition-all duration-200
                            ${formData.file_name === file.value
                              ? 'border-primary-600 bg-primary-50 shadow-md'
                              : 'border-gray-200 bg-white hover:border-primary-300 hover:shadow-sm'
                            }
                          `}
                        >
                          <div className="flex items-center space-x-3 mb-2">
                            <span className="text-3xl">{file.icon}</span>
                            <span className="font-semibold text-gray-900">
                              {file.label}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 text-left">
                            {file.description}
                          </p>
                          {formData.file_name === file.value && (
                            <div className="absolute top-2 right-2">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-600 text-white">
                                ✓
                              </span>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom du fichier téléchargé
                    </label>
                    <input
                      type="text"
                      value={formData.download_filename}
                      onChange={(e) => setFormData({ ...formData, download_filename: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="CV-John-Doe.pdf"
                    />
                  </div>
                </>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Génération...</span>
                  </>
                ) : (
                  <>
                    <span>📧</span>
                    <span>Générer un nouveau lien</span>
                  </>
                )}
              </button>
            </form>

            {error && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600">❌ {error}</p>
              </div>
            )}

            {generatedLink && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-2">
                  ✅ Lien généré avec succès !
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      readOnly
                      value={generatedLink.viewUrl}
                      className="flex-1 px-3 py-2 bg-white border border-green-300 rounded text-sm"
                    />
                    <button
                      onClick={copyToClipboard}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center space-x-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <span>Copier</span>
                    </button>
                  </div>
                  <p className="text-sm text-green-700">
                    ⏰ Expire le : {new Date(generatedLink.expiresAt).toLocaleString('fr-FR')}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Paramètres */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              ⚙️ Paramètres du visualiseur
            </h2>

            <div className="space-y-6">
              {/* Zoom par défaut */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Zoom par défaut
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="range"
                    min="50"
                    max="200"
                    step="10"
                    value={settings.defaultZoom}
                    onChange={(e) => handleSettingChange('defaultZoom', parseInt(e.target.value))}
                    className="flex-1"
                  />
                  <span className="text-lg font-semibold text-primary-600 min-w-[60px]">
                    {settings.defaultZoom}%
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Le PDF s'affichera à {settings.defaultZoom}% par défaut
                </p>
              </div>

              {/* Autoriser le téléchargement */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Autoriser le téléchargement
                  </label>
                  <p className="text-sm text-gray-500">
                    Les recruteurs pourront télécharger le PDF
                  </p>
                </div>
                <button
                  onClick={() => handleSettingChange('allowDownload', !settings.allowDownload)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.allowDownload ? 'bg-green-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.allowDownload ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Aperçu des paramètres */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  📋 Paramètres actuels
                </h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Zoom par défaut : <strong>{settings.defaultZoom}%</strong></li>
                  <li>• Téléchargement : <strong>{settings.allowDownload ? 'Autorisé' : 'Désactivé'}</strong></li>
                  <li>• Durée du token : <strong>48 heures</strong></li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Statistiques rapides */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">Tokens actifs</p>
                <p className="text-2xl font-bold text-gray-900">{stats.active_tokens}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">Vues totales</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_views}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">Téléchargements</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_downloads}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Liste des tokens */}
        <div className="mt-8">
          <TokenList
            tokens={tokens}
            onRevoke={handleRevoke}
            onExtend={handleExtend}
            onDelete={handleDelete}
            onUpdate={handleUpdate}
            onRefresh={handleRefresh}
            onShowFlash={showFlashMessage}
          />
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">
            💡 Comment ça marche ?
          </h3>
          <ol className="text-sm text-blue-800 space-y-2">
            <li>1. Remplissez le formulaire avec les informations du recruteur</li>
            <li>2. Cliquez sur "Générer le lien" pour créer un token JWT valide 48h</li>
            <li>3. Copiez le lien et envoyez-le par email au recruteur</li>
            <li>4. Le recruteur pourra visualiser votre CV de manière sécurisée</li>
            <li>5. Le lien expirera automatiquement après 48 heures</li>
            <li>6. Gérez vos tokens actifs : prolongez, révoquez ou copiez les liens</li>
          </ol>
        </div>
      </div>
      
      <Toast toast={toast} onClose={hideToast} />
    </div>
  );
}
